import type { NewTradeInConfiguration, OldTradeInConfiguration } from "@jti/rest-api-types";

import { useGetTradeInDevices } from "../useTradeInDevices";

import { useGetTradeInConfiguration } from "./useGetTradeInConfiguration";

type TradeInConfigurationOnlineData =
  OldTradeInConfiguration["data"]["online"] | NewTradeInConfiguration["data"]["online"];

type TradeInConfigurationResult = {
  isLoading: boolean;
  isUsingRegisteredDevices?: boolean;
  devices?: {
    deviceId: string;
    deviceName: string;
  }[];
};

const isOldFormat = (data: TradeInConfigurationOnlineData): data is OldTradeInConfiguration["data"]["online"] => {
  return Array.isArray(data.supportedModels);
};

export const useTradeInConfiguration = (): TradeInConfigurationResult => {
  const { data: tradeInConfigurationData, isLoading: isTradeInConfigLoading } = useGetTradeInConfiguration();
  const { data: eligibleRegisteredDevices, isLoading: isTradeInDevicesLoading } = useGetTradeInDevices();

  const onlineData = tradeInConfigurationData?.online;
  const isUsingRegisteredDevices = onlineData
    ? isOldFormat(onlineData)
      ? !onlineData.requiredDevice
      : onlineData.registeredDevicesOnly
    : undefined;

  if (!onlineData) {
    return {
      isLoading: isTradeInConfigLoading || isTradeInDevicesLoading,
    };
  }

  const defaultDevices = isOldFormat(onlineData) ? onlineData.supportedModels : onlineData.supportedModels.internal;

  const devices = isUsingRegisteredDevices
    ? eligibleRegisteredDevices?.data.map(({ deviceId, deviceName }) => ({
        deviceId,
        deviceName: deviceName ?? deviceId,
      })) || []
    : defaultDevices.map((device) => ({
        deviceId: device,
        deviceName: device,
      }));

  if (isOldFormat(onlineData)) {
    return {
      isLoading: isTradeInConfigLoading || isTradeInDevicesLoading,
      isUsingRegisteredDevices,
      devices,
    };
  }

  return {
    isLoading: isTradeInConfigLoading || isTradeInDevicesLoading,
    isUsingRegisteredDevices,
    devices,
  };
};
